import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';

import { useAuth, User } from '../context/AuthContext';

import { CreditWarningToastListener } from './CreditDisplay';
import { ProfileAvatar } from './ProfileAvatar';

// ── SVG Icons ───────────────────────────────────────────────────────

const ProfileIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="user-menu__icon">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

const ProjectsIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="user-menu__icon">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const BillingIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="user-menu__icon">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const TeamIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="user-menu__icon">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const LogoutIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="user-menu__icon">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const ExitIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="user-menu__icon">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

// ── Utility Hooks ───────────────────────────────────────────────────

const useClickOutside = (ref: React.RefObject<HTMLDivElement | null>, callback: () => void) => {
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) callback();
    };

    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);
  }, [ref, callback]);
};

const useProjectId = () => {
  const location = useLocation();
  const match =
    matchPath({ path: '/planning/:projectId' }, location.pathname) ||
    matchPath({ path: '/projects/:projectId/*' }, location.pathname);

  return match?.params.projectId;
};

const getImageUrl = (user: User) => user.profileImageUrl || user.avatarUrl || null;

// ── Shared Sub-Components ───────────────────────────────────────────

const UserInfo: React.FC<{ user: User }> = ({ user }) => (
  <div className="user-menu__info">
    <div className="user-menu__name">{user.displayName || user.email}</div>
    <div className="user-menu__email">{user.email}</div>
  </div>
);

interface MenuItemProps {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  className?: string;
  disabled?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({ onClick, icon, label, className = 'user-menu__item', disabled }) => (
  <button onClick={onClick} className={className} disabled={disabled}>
    {icon}{label}
  </button>
);

// ── Impersonated User Dropdown ──────────────────────────────────────

interface ImpersonatedDropdownProps {
  user: User;
  onNavigate: (path: string) => void;
  onTeamNavigate: () => void;
  onExitImpersonation: () => void;
}

const ImpersonatedDropdown: React.FC<ImpersonatedDropdownProps> = ({
  user, onNavigate, onTeamNavigate, onExitImpersonation,
}) => (
  <div className="user-menu__dropdown">
    <div className="impersonation-label">Viewing as</div>
    <UserInfo user={user} />
    <div className="user-menu__actions">
      <MenuItem onClick={() => onNavigate('/profile')} icon={<ProfileIcon />} label="Profile" />
      <MenuItem onClick={() => onNavigate('/projects')} icon={<ProjectsIcon />} label="My Projects" />
      <MenuItem onClick={() => onNavigate('/billing')} icon={<BillingIcon />} label="Billing" />
      <MenuItem onClick={onTeamNavigate} icon={<TeamIcon />} label="Team Members" />
      <div className="user-menu__divider" />
      <MenuItem
        onClick={onExitImpersonation}
        icon={<ExitIcon />}
        label="Exit Impersonation"
        className="impersonation-exit"
      />
    </div>
  </div>
);

// ── Admin User Dropdown ─────────────────────────────────────────────

interface AdminDropdownProps {
  user: User;
  isLoading: boolean;
  onLogout: () => void;
}

const AdminDropdown: React.FC<AdminDropdownProps> = ({ user, isLoading, onLogout }) => (
  <div className="user-menu__dropdown">
    <div className="impersonation-label impersonation-label--admin">Admin Account</div>
    <UserInfo user={user} />
    <div className="user-menu__actions">
      <MenuItem
        onClick={onLogout}
        icon={<LogoutIcon />}
        label={isLoading ? 'Logging out...' : 'Log out'}
        className="user-menu__logout"
        disabled={isLoading}
      />
    </div>
  </div>
);

// ── Navigation Handlers ─────────────────────────────────────────────

const useMenuNavigation = (
  setImpersonatedOpen: (v: boolean) => void,
  setAdminOpen: (v: boolean) => void,
) => {
  const navigate = useNavigate();
  const projectId = useProjectId();

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    setImpersonatedOpen(false);
    setAdminOpen(false);
  }, [navigate, setImpersonatedOpen, setAdminOpen]);

  const handleTeamNavigate = useCallback(() => {
    const teamPath = projectId ? `/projects/${projectId}/team` : '/team';

    handleNavigate(teamPath);
  }, [projectId, handleNavigate]);

  return { handleNavigate, handleTeamNavigate };
};

// ── Impersonated User Avatar ────────────────────────────────────────

const ImpersonatedAvatar: React.FC<{
  user: User;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: (path: string) => void;
  onTeamNavigate: () => void;
  onExitImpersonation: () => void;
  menuRef: React.RefObject<HTMLDivElement | null>;
}> = ({ user, isOpen, onToggle, onNavigate, onTeamNavigate, onExitImpersonation, menuRef }) => (
  <div ref={menuRef} className="impersonation-menu">
    <button
      onClick={onToggle}
      className="user-menu__button impersonation-button--impersonated"
      aria-label="Impersonated user menu"
    >
      <ProfileAvatar imageUrl={getImageUrl(user)} name={user.displayName || user.email} size="small" />
    </button>
    {isOpen && (
      <ImpersonatedDropdown
        user={user}
        onNavigate={onNavigate}
        onTeamNavigate={onTeamNavigate}
        onExitImpersonation={onExitImpersonation}
      />
    )}
  </div>
);

// ── Admin Avatar ────────────────────────────────────────────────────

const AdminAvatar: React.FC<{
  user: User;
  isOpen: boolean;
  isLoading: boolean;
  onToggle: () => void;
  onLogout: () => void;
  menuRef: React.RefObject<HTMLDivElement | null>;
}> = ({ user, isOpen, isLoading, onToggle, onLogout, menuRef }) => (
  <div ref={menuRef} className="impersonation-menu">
    <button
      onClick={onToggle}
      className="user-menu__button impersonation-button--admin"
      aria-label="Admin user menu"
    >
      <ProfileAvatar imageUrl={getImageUrl(user)} name={user.displayName || user.email} size="small" />
      <span className="impersonation-badge">👑</span>
    </button>
    {isOpen && <AdminDropdown user={user} isLoading={isLoading} onLogout={onLogout} />}
  </div>
);

// ── State Hook ──────────────────────────────────────────────────────

const useDropdownState = () => {
  const [impersonatedOpen, setImpersonatedOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const impersonatedRef = useRef<HTMLDivElement>(null);
  const adminRef = useRef<HTMLDivElement>(null);

  useClickOutside(impersonatedRef, () => setImpersonatedOpen(false));
  useClickOutside(adminRef, () => setAdminOpen(false));

  return { impersonatedOpen, setImpersonatedOpen, adminOpen, setAdminOpen, impersonatedRef, adminRef };
};

const useImpersonationActions = (
  setImpersonatedOpen: (v: boolean) => void,
  setAdminOpen: (v: boolean) => void,
) => {
  const { logout, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const { handleNavigate, handleTeamNavigate } = useMenuNavigation(setImpersonatedOpen, setAdminOpen);

  const handleExitImpersonation = useCallback(() => {
    exitImpersonation();
    setImpersonatedOpen(false);
    navigate('/admin/users');
  }, [exitImpersonation, navigate, setImpersonatedOpen]);

  const handleLogout = useCallback(async () => {
    try { exitImpersonation(); await logout(); } catch (e) { console.error('Logout failed:', e); }
  }, [exitImpersonation, logout]);

  return { handleNavigate, handleTeamNavigate, handleExitImpersonation, handleLogout };
};

// ── Main Component ──────────────────────────────────────────────────

export const ImpersonationUserMenu: React.FC = () => {
  const { user, isLoading, originalAdminUser } = useAuth();
  const ds = useDropdownState();
  const actions = useImpersonationActions(ds.setImpersonatedOpen, ds.setAdminOpen);

  if (!user || !originalAdminUser) return null;

  return (
    <div className="impersonation-menus">
      <ImpersonatedAvatar
        user={user}
        isOpen={ds.impersonatedOpen}
        onToggle={() => { ds.setImpersonatedOpen(!ds.impersonatedOpen); ds.setAdminOpen(false); }}
        onNavigate={actions.handleNavigate}
        onTeamNavigate={actions.handleTeamNavigate}
        onExitImpersonation={actions.handleExitImpersonation}
        menuRef={ds.impersonatedRef}
      />
      <AdminAvatar
        user={originalAdminUser}
        isOpen={ds.adminOpen}
        isLoading={isLoading}
        onToggle={() => { ds.setAdminOpen(!ds.adminOpen); ds.setImpersonatedOpen(false); }}
        onLogout={actions.handleLogout}
        menuRef={ds.adminRef}
      />
      <CreditWarningToastListener />
    </div>
  );
};
